/**
 * La visite guidée de la première session : un accueil, puis quatre bulles
 * posées sur l'interface réelle, qui racontent le cycle (passage, leads,
 * ciblage, veille). Deux variantes : le premier passage tourne encore
 * (« running », le cas normal en sortie d'onboarding) ou il est déjà fini
 * (« settled », retour après coup).
 */

import type { Messages } from "@shared/i18n";

export type TourVariant = "running" | "settled";
type Side = "top" | "right" | "bottom" | "left";

/** The step's copy lives in `shell.tour.steps`, under the same id. */
export type TourStepId = keyof Messages["shell"]["tour"]["steps"];

export type TourStep = {
  id: TourStepId;
  /** Cibles par ordre de préférence (la première présente l'emporte), par variante. */
  anchors: string[] | Record<TourVariant, string[]>;
  side: { desktop: Side; mobile: Side };
  align: "start" | "center" | "end";
};

export function pick<T>(value: T | Record<TourVariant, T>, variant: TourVariant): T {
  return value !== null && typeof value === "object" && !Array.isArray(value) && "running" in value
    ? (value as Record<TourVariant, T>)[variant]
    : (value as T);
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: "passage",
    anchors: {
      running: ['[data-tour="agent-steps"]', "#watch"],
      settled: ['[data-tour="pass-summary"]', "#watch"],
    },
    side: { desktop: "bottom", mobile: "bottom" },
    align: "start",
  },
  {
    id: "leads",
    // L'icône plutôt que le lien : le lien porte aussi le libellé replié, plus large que le rail.
    anchors: ['[data-tour="nav-leads"] svg'],
    side: { desktop: "right", mobile: "top" },
    align: "center",
  },
  {
    id: "ciblage",
    anchors: ['[data-tour="watch-offer"]', '[data-tour="nav-offer"] svg'],
    side: { desktop: "bottom", mobile: "bottom" },
    align: "end",
  },
  {
    id: "veille",
    anchors: ['[data-tour="watch-status"]', "#watch"],
    side: { desktop: "bottom", mobile: "bottom" },
    align: "end",
  },
];

/** Le dernier bouton rend la main à ce que la page montre : le passage en cours, ou son bilan (libellé : `shell.tour.lastAction`). */
export const TOUR_LAST_ACTION: Record<TourVariant, { focus: string }> = {
  running: { focus: '[data-tour="agent-steps"]' },
  settled: { focus: '[data-tour="pass-summary"]' },
};

/**
 * À la fin du premier passage suivi pendant la visite, une dernière bulle montre
 * la prochaine action réelle : les leads s'il y en a, le ciblage sinon.
 */
export function bilanNudge(newLeads: number, copy: Messages["shell"]["tour"]["nudge"]) {
  if (newLeads === 0) {
    return {
      anchors: ['[data-tour="watch-offer"]', '[data-tour="pass-summary"]'],
      body: copy.none,
      align: "end" as const,
    };
  }
  return {
    anchors: ['[data-tour="watch-primary"]', '[data-tour="pass-summary"]'],
    body: copy.leads(newLeads),
    align: "start" as const,
  };
}
